import type { FC } from "react";
import { useId } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { twMerge } from "tailwind-merge";

import { LabeledCheckbox } from "./LabeledCheckbox";

import { t } from "i18n/i18n";
import { SquareButton } from "mds/components/buttons/SquareButton";

interface AltTextFormProps {
  className?: string;
  defaultValues?: FormValues;
  onSubmit: (data: FormValues) => void;
  onCancel?: () => void;
}

interface FormValues {
  text: string;
  isDecorative: boolean;
}

const AltTextForm: FC<AltTextFormProps> = ({ className, defaultValues, onSubmit, onCancel }) => {
  const form = useForm<FormValues>({
    defaultValues: defaultValues ?? {
      text: "",
      isDecorative: false,
    },
    mode: "onBlur",
  });
  const { register, watch } = form;
  const isDecorative = watch("isDecorative");

  const textareaId = useId();
  const descriptionId = useId();

  return (
    <FormProvider {...form}>
      <form
        className={twMerge("flex flex-col gap-4", className)}
        onSubmit={form.handleSubmit(onSubmit)}
      >
        <label className="text-base font-medium text-black-tint-10" htmlFor={textareaId}>
          {t("asset_field.alt_text")}
        </label>
        <p className="text-sm font-normal text-black-tint-55" id={descriptionId}>
          {t("asset_field.alt_text_description")}
        </p>
        <textarea
          {...register("text")}
          aria-describedby={descriptionId}
          className="w-full rounded-md border border-black-tint-90 p-2"
          disabled={isDecorative}
          id={textareaId}
          placeholder={t("asset_field.alt_text_placeholder")}
          rows={3}
        />
        <LabeledCheckbox
          checkboxClassName="size-4"
          className="text-black-tint-20"
          registration={register("isDecorative")}
        >
          {t("asset_field.mark_as_decorative")}
        </LabeledCheckbox>

        <div className="mt-4 flex flex-row gap-2 self-end">
          <SquareButton kind="tertiary" size="m" onClick={onCancel}>
            {t("common.cancel")}
          </SquareButton>
          <SquareButton kind="accent" size="m" type="submit">
            {t("common.save")}
          </SquareButton>
        </div>
      </form>
    </FormProvider>
  );
};

// Using a default export to allow lazy loading
// eslint-disable-next-line import/no-default-export
export default AltTextForm;
